import React, { Fragment, useRef } from "react";
import * as StateLib from "@/Utils/StateLib";
import { ToolIcon, Menu } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { getFeatureAccess } from "@/Utils/Misc";
import { enumFeaturesList } from "@coreapp/constants";
import { useRouteState } from "@/hooks/useRouteState";
import { HeaderNavIcon } from "@/appComponents/Navigation/MainNavigation/HeaderNavIcon";

const ChangeChainLevel = target => {
  let doc = StateLib.GetDoc();
  let currentChainLevel = doc && doc.GetLoadedChainLevels();
  if (target.value !== currentChainLevel) {
    doc.SetRequestedChainLevels(target.value);
    let subchart = doc.GetSubChartMgr().GetCurrentSubChart();
    doc.SubChartSwitch(subchart);
  }
};

const BuildLevelOptions = setLevelsParam => {
  let maxLevels = 5;
  let levelOptions = [];
  for (let i = 2; i <= maxLevels; i++) {
    let levelOption = {};
    levelOption.label = i.toString() + " " + appstr("levels");
    levelOption.value = i;
    // URL-driven: write ?levels=N. useLevelsSync hook reads the URL
    // and drives doc.SetRequestedLevels + SubChartSwitch in coreapp,
    // so the canvas reloads at the new depth and the URL reflects it
    // (bookmarkable, shareable, refreshable).
    levelOption.clickHandler = target => setLevelsParam(String(target.value));
    levelOptions.push(levelOption);
  }
  return levelOptions;
};

const BuildChainOfCmdOptions = () => {
  let maxLevels = 3;
  let levelOptions = [];
  levelOptions.push({
    label: appstr("Off"),
    value: 0,
    clickHandler: ChangeChainLevel
  });
  for (let i = 1; i <= maxLevels; i++) {
    let levelOption = {};
    levelOption.label = i.toString() + " " + (i === 1 ? appstr("level") : appstr("levels"));
    levelOption.value = i;
    levelOption.clickHandler = ChangeChainLevel;
    levelOptions.push(levelOption);
  }
  levelOptions.push({
    label: appstr("alllevels"),
    value: 100,
    clickHandler: ChangeChainLevel
  });
  return levelOptions;
};

const NavigateHome = () => {
  let wsMgr = StateLib.GetWSMgr();

  let cmdElement = {};
  cmdElement["targetID"] = wsMgr.GetUserName();

  if (cmdElement["targetID"] != null) {
    cmdElement["queryID"] = cmdElement["targetID"];
    wsMgr.DoNavToBox(cmdElement);
    StateLib.ReRenderCanvas();
  }
};

const ChartNavigationComposite = () => {
  const levelOpenCtrlRef = useRef(null);
  const cocOpenCtrlRef = useRef(null);
  const { setParam: setRouteParam } = useRouteState();
  const setLevelsParam = value => setRouteParam("levels", value);

  const doc = StateLib.GetDoc();
  if (!doc) return;
  const subChartMgr = doc && doc.GetSubChartMgr();
  const currentSubChart = subChartMgr && subChartMgr.GetCurrentSubChart();
  let isServerChart = doc.IsServerChart();
  let level, chainLevel;

  if (currentSubChart) {
    level = doc.GetLoadedLevels();
    chainLevel = doc.GetLoadedChainLevels();
  }
  const levelHeader = {
    label: appstr("Levels down")
  };

  const levelTrigger = (
    <ToolIcon
      name={"level-down_"}
      title={appstr("Level down")}
      data-testid="chart-mode-toolbar-levels-down"
      tooltipProps={{ message: appstr("Levels down"), referenceOpenCtrlRef: levelOpenCtrlRef }}
      disabled={!getFeatureAccess(enumFeaturesList.levelsDown)}
    />
  );

  const chainOfCmdHeader = {
    label: appstr("Levels up")
  };

  const chainOfCmdTrigger = (
    <ToolIcon
      name={"level-up_"}
      title={appstr("Level up")}
      data-testid="chart-mode-toolbar-levels-up"
      tooltipProps={{ message: appstr("Levels up"), referenceOpenCtrlRef: cocOpenCtrlRef }}
      disabled={!getFeatureAccess(enumFeaturesList.levelsUp)}
    />
  );

  return (
    <Fragment>
      {isServerChart && (
        <Menu
          openCtrlRef={levelOpenCtrlRef}
          dataTestId={"ctb-levels-menu"}
          trigger={levelTrigger}
          position="bottom-start"
          listHeader={levelHeader}
          options={BuildLevelOptions(setLevelsParam)}
          value={level}
          listWidth={"small"}
        />
      )}
      {isServerChart && (
        <Menu
          openCtrlRef={cocOpenCtrlRef}
          dataTestId={"ctb-coc-menu"}
          trigger={chainOfCmdTrigger}
          position="bottom-start"
          listHeader={chainOfCmdHeader}
          options={BuildChainOfCmdOptions()}
          value={chainLevel}
          listWidth={"small"}
        />
      )}
      <ToolIcon
        id={"ctb-home-btn"}
        data-testid="chart-mode-toolbar-home"
        name={"home"}
        title={appstr("Home")}
        onClick={NavigateHome}
        tooltipProps={{ message: appstr("Home") }}
      />
      <HeaderNavIcon itemKey="directory" />
    </Fragment>
  );
};

export default ChartNavigationComposite;
